import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import { inviteService, getErrorMessage } from '../../../services/apiService';
import apiClient from '../../../services/apiService';

const InviteVendorModal = ({ isOpen, onClose, onSuccess, prefillData, requestId }) => {
  const toast = useToast();
  const [name, setName] = useState(prefillData?.name || '');
  const [email, setEmail] = useState(prefillData?.email || '');
  const [emailError, setEmailError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // 'sent' | 'failed' | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Email validation function
  const validateEmail = (email) => {
    if (!email) return '';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid Email Id format';
    }
    
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleCreateInvite = async () => {
    // Validate email before submission
    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    setLoading(true);
    setError('');
    setEmailError('');
    setInviteUrl('');
    try {
      let data;
      
      if (requestId) {
        // If requestId is provided, use supplier request invite endpoint
        const response = await apiClient.post(`/supplier-requests/${requestId}/invite-vendor`);
        data = response.data;
      } else {
        // Use standard invite service
        const response = await inviteService.createInvite({ name, email });
        data = response?.data || response;
      }
      
      setInviteUrl(data.invite_url);
      if (data.email_sent === false) {
        setEmailStatus('failed');
        setError(data.error_message || 'Email sending failed');
        toast.warning('Invite created but email failed to send. Share the link manually.');
      } else {
        setEmailStatus('sent');
        toast.success(`Invitation email sent to ${email}`);
      }
      
      // Call onSuccess if provided
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (e) {
      console.error('Failed to create invite:', e);
      const errorMsg = getErrorMessage(e);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard!');
    } catch (_) {
      toast.error('Failed to copy link. Please select and copy manually.');
    }
  };

  // Support both controlled (isOpen) and uncontrolled modes
  if (isOpen === false) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" onClick={onClose} />
      <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-900">Invite Vendor</h3>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            {requestId && (
              <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Icon name="Info" size={16} className="inline mr-2" />
                Inviting vendor from approved supplier request
              </div>
            )}
            <Input
              label="Vendor Name"
              type="text"
              placeholder="Acme Corp"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!requestId}
              required
            />
            <Input
              label="Vendor Email"
              type="email"
              placeholder="vendor@example.com"
              value={email}
              onChange={handleEmailChange}
              disabled={!!requestId}
              required
              error={emailError}
            />
            {error && <div className="text-sm text-red-600">{error}</div>}
            {inviteUrl && (
              <div className="space-y-2">
                <div className="text-sm text-slate-600">Invite Link</div>
                <div className="flex items-center gap-2">
                  <input className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" readOnly value={inviteUrl} />
                  <Button size="sm" onClick={copyLink} iconName="Copy">Copy</Button>
                </div>
                {emailStatus === 'sent' && (
                  <div className="text-xs text-emerald-600">Email sent to {email}.</div>
                )}
                {emailStatus === 'failed' && (
                  <div className="text-xs text-amber-600">Email could not be sent. Share the link above manually.</div>
                )}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <button 
              onClick={onClose}
              className="px-5 py-2.5 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              Close
            </button>
            <button 
              onClick={handleCreateInvite} 
              disabled={loading || !email || !name || !!emailError}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating...
                </>
              ) : (
                <span>Generate Link</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InviteVendorModal;
